// ═══════════════════════════════════════════════════════════════════════════
// deepLink.js — opening the desktop app from the dashboard.
//
// Two schemes, registered by the desktop app
// (electron-builder.json5 → schemes: ["npqvpn"]):
//
//   npqvpn://open              just raise the window
//   npqvpn://login?code=…      raise the window AND sign in as this user
//
// Both rely on the macOS open-url / Windows+Linux second-instance handlers, so
// they focus an already-running app rather than starting a second copy.
//
// The login code comes from POST /auth/desktop-handoff: single-use, 60-second
// TTL, and not a session by itself — the desktop app exchanges it for real
// tokens at POST /auth/handoff. See docs/desktop-handoff.md in the enterprise
// backend for the full contract.
// ═══════════════════════════════════════════════════════════════════════════
import authService from "../../services/authService";

export const DEEP_LINK = "npqvpn://open";

/** Focus (or launch) the desktop app. Silently no-ops if the scheme isn't registered. */
export function openDesktopApp() {
  window.location.href = DEEP_LINK;
}

/**
 * Mint a hand-off code and open the app straight into a signed-in session.
 *
 * Falls back to a plain `npqvpn://open` if minting fails — a user who can't get
 * the automatic sign-in should still land in the app and be able to type their
 * password, rather than have the button do nothing.
 *
 * @returns {Promise<{ ok: boolean, error?: string }>} ok:false means the code
 *          could not be minted and the plain link was opened instead.
 */
export async function openDesktopAppWithLogin() {
  try {
    const { deep_link: deepLink, code } = await authService.createDesktopHandoff();

    // Prefer the link the backend built (keeps the scheme in one place), but
    // tolerate an older backend that only returns the raw code.
    const target = deepLink || `npqvpn://login?code=${encodeURIComponent(code)}`;
    if (!deepLink && !code) throw new Error("No hand-off code in response");

    window.location.href = target;
    return { ok: true };
  } catch (err) {
    // Never surface the code or the raw error to the console — this path is
    // reached with a live credential in flight.
    openDesktopApp();
    return {
      ok: false,
      error:
        err?.status === 429
          ? "Too many attempts. Wait a moment and try again."
          : "Couldn't sign you in automatically — opening the app so you can sign in there.",
    };
  }
}
